
import Vue from 'vue';

import { PinturaSidebarHeader } from '../sidebar-header';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useSectionStore } from '@/stores/section';
import { useStore as useNotificationStore } from '@/stores/notification';
import { useStore as useMirrorStore } from '@/stores/mirror';
import { useStore as useShapeStore } from '@/stores/shape';
import { useStore as useHistoryStore } from '@/stores/history';
import { mapActions, mapState, mapStores } from 'pinia';
import { CvModal, CvButton } from '@carbon/vue';
import { useStore as useColorsManagementStore } from '@/stores/colorsManagement';
import PrintPositionsManager from '../../../print-positions-manager/print-positions-manager.vue';
import { generateEventPayload } from '@/utils/generatePriceParamsEventPayload';
import { ProductPrintPositionAddModel } from '@/typings/ProductPrintData';
import { PropType } from 'vue';
import { PdpChoicesSetterType } from '@/setters/typings';
import { useStore as useLangStore } from '@/stores/lang';
import { createStateSnapshot } from '@/utils/historyHelper';

export default Vue.extend({
  name: 'PinturaSidebar',
  props: {
    pdpChoicesData: {
      type: Object as PropType<PdpChoicesSetterType>,
      required: true,
    },
    sapCustomerId: {
      type: String,
      required: true,
    },
    tok: {
      type: String,
      required: true,
    },
    context: {
      type: String,
      required: false,
      default: '',
    },
  },
  directives: {
    DynamicEvents: {
      bind: function (el, binding, vnode) {
        const allEvents = binding.value;
        allEvents.forEach((event: string) => {
          vnode.componentInstance?.$on(event, (eventData: unknown) => {
            (
              vnode.context as Vue & {
                proxyEvent: (event: string, eventData: unknown) => void;
              }
            )?.proxyEvent(event, eventData);
          });
        });
      },
      unbind: function (el, binding, vnode) {
        vnode.componentInstance?.$off();
      },
    },
  },
  components: {
    PinturaSidebarHeader,
    PrintPositionsManager,
    CvButton,
    CvModal,
    'shape-box-size': () =>
      import(
        /* webpackChunkName: "sidebar-sections" */ '../sections/section-a/ShapeBoxSize.vue'
      ),
    'color-management': () =>
      import(
        /* webpackChunkName: "sidebar-sections" */ '../sections/color-management/ColorManagement.vue'
      ),
    'background-type-selection': () =>
      import(
        /* webpackChunkName: "sidebar-sections" */ '../sections/section-b/BackgroundTypeSelection.vue'
      ),
    'print-position-box': () =>
      import(
        /* webpackChunkName: "sidebar-sections" */ '../sections/section-c/PrintPositionBox.vue'
      ),
    'price-calc': () =>
      import(
        /* webpackChunkName: "sidebar-sections" */ '../sections/price-calc-section/PriceCalc.vue'
      ),
    'confirm-and-order': () =>
      import(
        /* webpackChunkName: "sidebar-sections" */ '../sections/confirm-and-order-section/ConfirmAndOrder.vue'
      ),
    'ruler-settings': () =>
      import(
        /* webpackChunkName: "ruler-settings" */ '../sections/ruler-settings/RulerSettings.vue'
      ),
  },
  data: () => {
    return {
      showBackgroundOptions: false,
      editId: '',
      deleteId: '',
      isSidebarDisabled: false,
      yesNoDialog: false,
      knownEvents: [
        'action:edit',
        'action:delete',
        'wizard:price-params-changed',
        'action:disable-pintura',
        'action:change-color',
        'action:trigger-small-details',
        'action:update-is-concept',
        'ruler:show-horizontal',
        'ruler:show-vertical',
      ],
    };
  },
  computed: {
    ...mapState(useProductStore, [
      'selectedPrintPosition',
      'selectedVariant',
      'unavailablePrintPositions',
      'isProductPrintable',
      'data',
      'printPositions',
      'activePosition',
    ]),
    ...mapStores(useSectionStore, useNotificationStore),
    ...mapState(useSectionStore, ['activeSections', 'sections', 'appContext']),
    ...mapState(useShapeStore, ['activeShapeId', 'shapes', 'positionShapeMap']),
    ...mapState(useLangStore, ['languageInUse']),
    techniqueNameTranslated(): string {
      const position = this.printPositions?.find(
        (p) => p.id === this.selectedPrintPosition.id
      );
      const technique = position?.printTechniques.find((technique) => {
        return technique.id === this.selectedPrintPosition.techniqueId;
      });
      const techniqueName = technique?.printTechnique.nameTranslations.find(
        ({ language }) => language.code === this.languageInUse
      )?.text;
      return techniqueName || this.selectedPrintPosition.techniqueName || '';
    },
  },
  methods: {
    log: console.log,
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useProductStore, [
      'deletePrintPosition',
      'setPreviousManagedPrintPositionId',
      'drawPoints',
    ]),
    ...mapActions(useColorsManagementStore, ['getAvailablePmsColors']),
    ...mapActions(useMirrorStore, ['removePositionMirrorSettings']),

    showConfirmDialog(id = '') {
      this.deleteId = id;
      this.yesNoDialog = true;
    },
    isDisabledForText(section: any) {
      const activeArtworkPayload = this.shapes.find(
        (shape) => shape.id === this.activeShapeId
      );
      return (
        section.component === 'background-type-selection' &&
        activeArtworkPayload === undefined
      );
    },

    openModal(id = '') {
      this.editId = id;
      this.$modal.show('print-positions-modal');
    },

    proxyEvent(event: string, eventData: unknown): void {
      console.groupEnd();
      if (event === 'action:edit') {
        this.setPreviousManagedPrintPositionId(eventData as string);
        this.openModal(eventData as string | undefined);
      }
      if (event === 'action:delete') {
        this.showConfirmDialog(eventData as string | undefined);
      }
      if (event === 'action:disable-pintura') {
        this.$emit('pintura:disable');
        this.isSidebarDisabled = true;
      }
      if (event === 'wizard:price-params-changed') {
        // NOTE: Should emit for every wizards action that in respect changes the value of one of the event payloads fields
        //  Wizard actions (what they change):
        //    4. Upload new logo (width, height, printTechniqueSAPRangeCode, numberOfColors) x
        //    7. Remove colour (numberOfColors) x

        this.$emit('wizard:price-params-changed', eventData);
      }
      if (event === 'action:change-color') {
        const event = eventData as ProductPrintPositionAddModel[];

        this.$emit(
          'action:change-color',
          event,
          event[0].positionId,
          event[0].positionId
        );
      }

      if (event === 'action:trigger-small-details') {
        this.$emit('action:trigger-small-details');
      }

      if (event === 'action:update-is-concept') {
        this.$emit('action:update-is-concept', eventData);
      }

      if (event === 'ruler:show-vertical') {
        this.$emit('ruler:show-vertical', eventData as boolean);
      }
      if (event === 'ruler:show-horizontal') {
        this.$emit('ruler:show-horizontal', eventData as boolean);
      }
    },

    async onDeletePrintPosition(deleteId: string) {
      await this.deletePrintPosition(deleteId);

      this.removePositionMirrorSettings(deleteId);

      const eventPayload = generateEventPayload();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      await this.getAvailablePmsColors();

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },
  },
});
